import { clearUserInfo } from '../config/auth-enumerations'
import { URLConfig } from '../components/global-nav/configs/base'
import { GlobalNav } from '../components/global-nav/global-nav'

export interface Languages {
	[key: string]: {
		languageCode: string
		url: string
		default?: boolean
	}
}
export type Language = { languageCode: string; url: string; default?: boolean }

export class LanguageService {
	private _currentLang: Language
	private _availableLanguages: Languages
	private _initialized = false

	constructor(private _urlConfig: URLConfig) {}

	// Public Methods

	/**
	 * Initializes the language selector service, if not the language service has not been initialized yet.
	 * @param languages - languages that the language selector supports
	 */
	public init(languages: Languages): void {
		if (!this._initialized) {
			this._availableLanguages = languages
			for (const lang in this._availableLanguages) {
				// eslint-disable-next-line no-prototype-builtins
				if (this._availableLanguages.hasOwnProperty(lang) && this._availableLanguages[lang].default === true) {
					this._currentLang = this._availableLanguages[lang]
					break
				}
			}
			//Sets the current language to the first language in the Languages array
			if (
				this._currentLang == undefined &&
				this._availableLanguages != undefined &&
				Object.keys(this._availableLanguages).length >= 1
			) {
				const firstKey = Object.keys(this._availableLanguages)[0]
				this._currentLang = this._availableLanguages[firstKey]
			}
			this._initialized = true
		}
	}

	public get isInitialized(): boolean {
		return this._initialized
	}

	/**
	 * Returns the current language selected by the Language Service
	 * E.g. en, fr, ar, etc.
	 */
	public get currentLanguage(): Language {
		return this._currentLang
	}

	/**
	 * Returns all currently available Languages initialized with the language service
	 */
	public get availableLanguages(): Languages {
		if (this._initialized) {
			return this._availableLanguages
		} else {
			return undefined
		}
	}

	/**
	 * Adds the language setting to the list of available languages if it is not yet included
	 * Also updates the current default language if one does not exist yet
	 * @param lang - Language type to be added to the language service
	 */
	public addLanguage(lang: Language): void {
		if (this._initialized) {
			if (lang !== null && lang !== undefined) {
				const langKey = lang.languageCode.toUpperCase()
				// eslint-disable-next-line no-prototype-builtins
				if (!this._availableLanguages.hasOwnProperty(langKey)) {
					this._availableLanguages[langKey] = lang
					if (this._currentLang === undefined || this._currentLang === null) {
						this._currentLang = this._availableLanguages[langKey]
					}
				} else {
					window.console.log('Language already exists in available languages')
				}
			}
		} else {
			window.console.error('Language Service has not been initialized yet')
		}
	}

	/**
	 * Switches the current language of the language selector into the selected language
	 * @param lang - string representing the language to be selected from the available language keys
	 */
	public toggleLanguage(lang: string): void {
		//Check to see if the language service is initialized first
		if (this._initialized) {
			//Check first to see if the language exists in the service
			// eslint-disable-next-line no-prototype-builtins
			if (this._availableLanguages.hasOwnProperty(lang.toUpperCase())) {
				//Do language toggle stuff here
				if (this._availableLanguages[lang.toUpperCase()] === this._currentLang) {
					//Do nothing if the language to toggle to is already selected
					window.console.log(`Language ${lang} is currently selected`)
				} else {
					const targetLanguage = this._availableLanguages[lang.toUpperCase()]
					const langCode = targetLanguage.languageCode
					const currentLangCode = this._currentLang.languageCode
					const pathname = window.location.pathname
					const search = window.location.search
					const hash = window.location.hash

					/**
					 * Use regex to capture language code used in path. This can differ depending on site.
					 * RegExp(`([a-z]{2}-)?(${currentLangCode})`, 'i') will match either a two character language
					 * code, or country-lang charactor code separated by a hyphen where each code is two characters.
					 *
					 * Working examples:
					 * 	- en (lang)
					 * 	- ca-en (country-lang)
					 * Not working example:
					 *  - can-en
					 */
					// eslint-disable-next-line no-useless-escape
					const langRegex = new RegExp(`\/([a-z]{2}-)?(${currentLangCode})`, 'i')
					const pathMatch = pathname.match(langRegex)

					if (!pathMatch) {
						this._currentLang = targetLanguage
						this._clearUserInfoSafely()
						window.location.replace(targetLanguage.url)
						return
					}

					let currentLangCodeFromPath = pathMatch[0].slice(1)
					let updatedPath = pathname.replace(langRegex, `/$1${langCode}`)

					if (updatedPath.endsWith(`/${currentLangCodeFromPath}`)) {
						updatedPath =
							updatedPath.substr(0, updatedPath.lastIndexOf(`/${currentLangCodeFromPath}`)) +
							`/${currentLangCodeFromPath.replace(langRegex, `$1${langCode}`)}`
					}
					//Set the current language to the toggled language
					this._currentLang = targetLanguage
					currentLangCodeFromPath = currentLangCodeFromPath.replace(`${currentLangCode}`, `${langCode}`)
					this._clearUserInfoSafely()
					//Create the url string that contains the current language's domain appended with the updated path
					const langPattern = '/' + currentLangCodeFromPath

					//Handle use cases such as
					//<domain>/<language-code> + <language-code>/..pathName
					const domainURL = this._currentLang.url.endsWith('/')
						? this._currentLang.url.substring(0, this._currentLang.url.length - 1)
						: this._currentLang.url

					if (updatedPath.startsWith(langPattern) && this._currentLang.url.endsWith(langPattern)) {
						//Handle use cases for starting language code with various extensions
						const startCodePattern = '/' + currentLangCodeFromPath + '/'
						const startingHashPattern = '/' + currentLangCodeFromPath + '#'
						const startingQueryPattern = '/' + currentLangCodeFromPath + '?'
						const startingExtensionPattern = '/' + currentLangCodeFromPath + '.'
						if (
							updatedPath.startsWith(startCodePattern) ||
							updatedPath.startsWith(startingHashPattern) ||
							updatedPath.startsWith(startingQueryPattern) ||
							updatedPath.startsWith(startingExtensionPattern)
						) {
							const pathForwardSlashIndex = updatedPath.indexOf('/', 1)
							updatedPath =
								pathForwardSlashIndex > -1
									? updatedPath.substr(pathForwardSlashIndex)
									: updatedPath.substr(3)
						}
						//Handle Use case for  path with just the language code
						if (updatedPath === `/${currentLangCodeFromPath}`) {
							updatedPath = ''
						}
					}
					//Get rid an empty path
					if (updatedPath === '/') {
						updatedPath = ''
					}
					const combinedURL = domainURL + updatedPath + search + hash
					window.location.replace(combinedURL)
				}
			} else {
				window.console.error(`Language ${lang} is not available in language service`)
			}
		} else {
			window.console.error('Language service has not been initialized')
		}
	}

	public attachLanguageLinkHandlers(globalNav: GlobalNav): void {
		const languageLinkList: HTMLElement[] = Array.from(globalNav.shadowRoot.querySelectorAll('a[mporgnav]'))
		languageLinkList.forEach(languageLink => {
			languageLink.onclick = (event: MouseEvent) => {
				event.preventDefault()
				const languageCode = languageLink.getAttribute('data-lang')
				if (languageCode && languageCode !== '') {
					window.gbpe.languageService.toggleLanguage(languageCode.toUpperCase())
				}
			}
		})
	}

	/** @deprecated Use {@link attachLanguageLinkHandlers} instead. */
	public handleLanguageLinks = (globalNav: GlobalNav): void => {
		this.attachLanguageLinkHandlers(globalNav)
	}

	private _clearUserInfoSafely(): void {
		try {
			clearUserInfo(this._urlConfig)
		} catch (error) {
			window.console.warn('Unable to clear user info while switching languages', error)
		}
	}
}
